import { gql, GraphQLClient } from 'graphql-request';
import { IncomingMessage } from 'http';
/**
 * These are our GraphQL endpoints for the different environments. We have 2 categories:
 *    1. Browser - called from the public internet, gets auth and other request context processed by pitstop. Called via browser
 *    2. Server - These *_GQL_SERVER_ENDPOINT variables are ONLY called on our private internet/ server side NextJS.
 *          * allows the traffic to stay internal to our network, saving us money and latency
 *          * allows us to forward x-strava-* headers so we can rate limit based on the true client IP as
 *            opposed to the IP of our Kubernetes node that the web server is running on.
 *
 * Each category has a url that points to a different graph variant. Client, staging, or current.
 * Check out the confluence page for more info https://strava.atlassian.net/l/cp/0PSj241M
 */

// endpoints to be called on the client side of the browser
export const CLIENTS_GQL_ENDPOINT = 'https://graphql.strava.com';
export const STAGING_GQL_ENDPOINT = 'https://graphql.staging.strava.com';
export const STAGING_ADMIN_GQL_ENDPOINT =
  'https://www.staging.strava.com/admin/graphql';
export const CURRENT_ADMIN_GQL_ENDPOINT =
  'https://admin.strava.com/admin/graphql';

// internal router endpoints to only be called within our network. Used on Server Side NextJS
export const CLIENTS_GQL_SERVER_ENDPOINT =
  'http://prod.router.graphql.discovery.svc.strava.com/';
export const STAGING_GQL_SERVER_ENDPOINT =
  'http://staging.router.graphql.discovery.svc.strava.com/';

export const getGraphEndpoint = (
  isAdmin: boolean,
  isStaging: boolean,
  isServerSide: boolean
) => {
  // admin does not differentiate between server or browser
  if (isAdmin) {
    return isStaging ? STAGING_ADMIN_GQL_ENDPOINT : CURRENT_ADMIN_GQL_ENDPOINT;
  }
  // if we are on the server, use the internal endpoints (reasoning above)
  if (isServerSide) {
    return isStaging
      ? STAGING_GQL_SERVER_ENDPOINT
      : CLIENTS_GQL_SERVER_ENDPOINT;
  }
  return isStaging ? STAGING_GQL_ENDPOINT : CLIENTS_GQL_ENDPOINT;
};

// this is the shape of the request object in GetServerSidePropsContext
export interface StravaGQLRequest extends IncomingMessage {
  cookies: Partial<Record<string, string>>;
}

/**
 * gets cookies from request.
 * @param {Object} req - request object from getServerSideProps
 * @returns the constructed auth cookie string
 */
export const getCookies = (req: StravaGQLRequest) => {
  const cookies = req.headers && req.headers.cookie ? req.headers.cookie : '';
  return cookies;
};

/**
 * Returns object containing all x-strava-* headers from the req.headers
 * @param {object} req - request object from getServerSideProps
 * @returns {object} - Object containing all x-strava-* headers or empty object when there are not matches
 */
export const getStravaHeaders = (req: StravaGQLRequest) =>
  Object.keys(req?.headers ?? {})
    .filter((key) => /x-strava-/.test(key.toLowerCase()))
    .reduce(
      (acc, key) => {
        acc[key] = req.headers[key as keyof typeof req.headers] as string;
        return acc;
      },
      {} as Record<string, string>
    );

/**
 * use the built graphql client with its given configuration and parameters to call the graphql endpoint and return the data or error.
 * @param {GraphQLClient} gqlClient - the GQL client built with the desired configurations
 * @param {string} query - the graphql query string
 * @param {Object} variables - the additional graphql variables to be passed
 * @returns the response object from the graphql request
 */
// Set default generic type to any, so it can be used without explicit typing. Existing code that don't use generics still work.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const callWithClient = async <T = any>(
  gqlClient: GraphQLClient,
  query: string,
  variables = {}
): Promise<T> => {
  const data = await gqlClient
    .request<T>(
      gql`
        ${query}
      `,
      variables
    )
    .catch((e) => {
      /**
     * GraphQLClient JS Error shape example:
        ClientError: 'sampleMessage': {
          response: {
            errors: [ {message: "sampleMessage", extensions:{code:"FORBIDDEN"} ],
            status: 500,
            headers: Headers {}
          },
          request: {
            query:
              query Query {},
            variables: {}
          }
        }
     */
      const { errors: graphQLErrors = [], error: networkError } =
        e.response || {};
      const graphQLError = graphQLErrors[0] || {};
      const { extensions = null } = graphQLError;
      const errorMessage =
        // error when calling the gql server
        networkError ||
        // errors with the gql query (ex: misspelled field name)
        (extensions && extensions?.message) ||
        (graphQLError && graphQLError?.message) ||
        // error with the client
        e.message ||
        // fallback error message
        'There was a problem with the GraphQL request';

      const errorOptions = graphQLErrors || e;

      let cause: string;

      try {
        cause = JSON.stringify(errorOptions);
      } catch {
        cause = String(errorOptions);
      }

      throw new Error(errorMessage, { cause });
    });
  return data;
};

// The 'apollographql-client-name' request header is passed in with all
// client and server side requests. It allows us to accurately attribute
// graphql requests as having come from our various web apps.
// https://www.apollographql.com/docs/graphos/metrics/client-awareness/#setup
export const DEFAULT_APOLLO_GRAPHQL_CLIENT_NAME = 'strava-web';
export const getApolloGraphqlClientName = () => {
  // Check process.env.APOLLO_GRAPHQL_CLIENT_NAME to see if a unique client
  // name is defined. If not, fall back to DEFAULT_APOLLO_GRAPHQL_CLIENT_NAME.
  // This is wrapped in a try/catch so that in the case where process is not
  // defined, we will ignore the "process is not defined" error to keep it
  // from bubbling up to the client and blocking requests and just return
  // the default client name.
  try {
    const clientName = process.env.APOLLO_GRAPHQL_CLIENT_NAME;
    return clientName ?? DEFAULT_APOLLO_GRAPHQL_CLIENT_NAME;
  } catch {
    // process isn't defined, ignore error
  }

  return DEFAULT_APOLLO_GRAPHQL_CLIENT_NAME;
};

// Set default generic type to any, so it can be used without explicit typing. Existing code that don't use generics still work.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const serverSideRequest = <T = any>(
  req: StravaGQLRequest,
  query: string,
  variables = {},
  isAdmin = false
): Promise<T> => {
  // call staging endpoint for localhost and staging
  const reqHost = req.headers.host;
  // webapp is included in this list for when we run the app using docker-compose and for Cypress tests
  const useStagingGql = ['staging', 'localhost', 'webapp', '::1'].some(
    (host) => reqHost?.includes(host) // if reqHost is undefined useStaging gql will be false
  );

  const useNextProxyEndpoint = ['localhost'].some((host) =>
    reqHost?.includes(host)
  );

  // if on NextJS local development on the server side, use the public endpoints
  // we do not get strava headers on local development since we hit the nextjs local server
  // instead of through the strava servers.
  let useServerSideEndpoints = true;
  if (process.env.NODE_ENV === 'development') {
    useServerSideEndpoints = false;
  }

  let endpoint = getGraphEndpoint(
    isAdmin,
    useStagingGql,
    useServerSideEndpoints
  );

  // when developing locally, we need to use the dev-proxy
  if (process.env.NODE_ENV === 'development' && useNextProxyEndpoint) {
    endpoint = `http://${
      reqHost ? encodeURI(reqHost) : 'localhost:3000'
    }/dev-proxy/${isAdmin ? 'admin-' : ''}graphql`;
  }

  const stravaHeaders = getStravaHeaders(req);

  const gqlClient = new GraphQLClient(endpoint, {
    headers: {
      Cookie: getCookies(req),
      Accept: 'application/json',
      'apollographql-client-name': getApolloGraphqlClientName(),
      ...stravaHeaders
    }
  });
  return callWithClient<T>(gqlClient, query, variables);
};

// Set default generic type to any, so it can be used without explicit typing. Existing code that don't use generics still work.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const clientSideRequest = <T = any>(
  query: string,
  variables = {},
  isAdmin = false
): Promise<T> => {
  const { location } = window;

  const hostname = location.hostname;
  // This addition is a temporary fix to enabled community management to view
  // the admin graphql data on admin.strava.com. Web Platform is having talks with
  // integrity and others to determine the proper way to handle admin feature access
  // see the PR description for more info
  if (!isAdmin && hostname.match(/(admin)/g)) {
    return gqlAdminRequest<T>(query, variables);
  }
  const useStagingGql = ['staging', 'localhost', '::1'].some((host) =>
    hostname.includes(host)
  );

  const isServerSide = false;
  let endpoint = getGraphEndpoint(isAdmin, useStagingGql, isServerSide);

  const useNextProxyEndpoint = ['localhost'].some(
    (host) =>
      typeof window !== 'undefined' && window.location.hostname.includes(host)
  );
  // when developing locally, we need to use the dev-proxy
  // middleware to prevent CORS on the client side.
  // We only want to use the dev-proxy endpoint for Next apps. Using this
  // endpoint to run MFEs locally will not work.
  if (process.env.NODE_ENV === 'development' && useNextProxyEndpoint) {
    endpoint = `http://${window.location.host}/dev-proxy/${
      isAdmin ? 'admin-' : ''
    }graphql`;
  }

  const graphQLClient = new GraphQLClient(endpoint, {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'apollographql-client-name': getApolloGraphqlClientName()
    }
  });

  return callWithClient<T>(graphQLClient, query, variables);
};

/**
 * Makes a GraphQL request to internal admin graph using the req object passed in from getServerSideProps
 * this is needed since the include credentials flag only works on the client side
 * should only be used when the call is made on the server side through getServerSideProps in NextJS.
 * @param {Object} req - the request object from getServerSideProps
 * @param {string} query - the graphql query string
 * @param {Object} variables - the additional graphql variables to be passed
 * @returns the graphql response retrieved with the given query and auth information
 */
// Set default generic type to any, so it can be used without explicit typing. Existing code that don't use generics still work.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const gqlAdminServerSideRequest = <T = any>(
  req: StravaGQLRequest,
  query: string,
  variables = {}
): Promise<T> => {
  return serverSideRequest<T>(req, query, variables, true);
};

/**
 * Makes a GraphQL request using the req object passed in from getServerSideProps
 * this is needed since the include credentials flag only works on the client side
 * should only be used when the call is made on the server side through getServerSideProps in NextJS.
 * @param {Object} req - the request object from getServerSideProps
 * @param {string} query - the graphql query string
 * @param {Object} variables - the additional graphql variables to be passed
 * @returns the graphql response retrieved with the given query and auth information
 */
// Set default generic type to any, so it can be used without explicit typing. Existing code that don't use generics still work.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const gqlServerSideRequest = <T = any>(
  req: StravaGQLRequest,
  query: string,
  variables = {}
): Promise<T> => {
  return serverSideRequest<T>(req, query, variables, false);
};

/**
 * Calls the internal admin graph if the user has the proper credentials and if it is on admin.strava.com + behind VPN
 * Perfect for admin microfrontends
 * @param {string} query - the graphql query string
 * @param {Object} variables - the additional graphql variables to be passed
 * @returns the graphql response retrieved with the given query and auth information
 */
// Set default generic type to any, so it can be used without explicit typing. Existing code that don't use generics still work.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const gqlAdminRequest = <T = any>(
  query: string,
  variables = {}
): Promise<T> => {
  return clientSideRequest<T>(query, variables, true);
};

/**
 * Makes a graphql request on the client.
 * Infers the credentials from the browser to authenticate.
 * Perfect for athlete facing microfrontends
 * @param {string} query - the graphql query string
 * @param {Object} variables - the additional graphql variables to be passed
 * @returns the graphql response retrieved with the given query
 */
// Set default generic type to any, so it can be used without explicit typing. Existing code that don't use generics still work.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const gqlRequest = <T = any>(
  query: string,
  variables = {}
): Promise<T> => {
  return clientSideRequest<T>(query, variables, false);
};

export { gql };
